import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { SearchIcon, XIcon } from '../AriesIcons';

/**
 * AriesSearch — the shared search box for the Aries UI redesign. A rounded pill
 * with a leading SearchIcon, an input, and a trailing clear (XIcon) that shows
 * when there is text. Search fires on Enter (and on the leading icon click).
 *
 * This is the single source of the redesign search look so every surface is
 * identical. It is intentionally separate from the legacy `components/SearchBar`
 * (kept unchanged for non-redesigned screens).
 */
const AriesSearch = ({
  searchValue,
  isCrossVisible,
  onChange,
  onSearch,
  placeholder,
  className,
  id,
}) => {
  const intl = useIntl();
  const placeholderText = intl.formatMessage({
    id: placeholder,
    defaultMessage: placeholder,
  });
  const showClear =
    typeof isCrossVisible === 'boolean' ? isCrossVisible : Boolean(searchValue);

  return (
    <div id={id} className={`aries-search ${className}`}>
      <button
        type="button"
        className="aries-search-icon"
        aria-label={intl.formatMessage({
          id: 'Search',
          defaultMessage: 'Search',
        })}
        onClick={onSearch}
      >
        <SearchIcon size={18} />
      </button>
      <input
        type="text"
        className="aries-search-input"
        placeholder={placeholderText}
        aria-label={placeholderText}
        autoComplete="off"
        value={searchValue ?? ''}
        onChange={onChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && searchValue) {
            e.preventDefault();
            onSearch();
          }
        }}
      />
      {showClear && (
        <button
          type="button"
          className="aries-search-clear"
          aria-label={intl.formatMessage({
            id: 'Clear',
            defaultMessage: 'Clear',
          })}
          onClick={() => onChange({ target: { value: '' } })}
        >
          <XIcon size={16} />
        </button>
      )}
    </div>
  );
};

AriesSearch.propTypes = {
  searchValue: PropTypes.string,
  isCrossVisible: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Receives an input-like event ({ target: { value } }). */
  onChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
};

AriesSearch.defaultProps = {
  searchValue: '',
  isCrossVisible: undefined,
  onSearch: () => {},
  placeholder: 'Search',
  className: '',
  id: '',
};

export default AriesSearch;
